import React from "react";
import { Button } from "react-bootstrap";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import RetroCard from "./RetroCard";

// Una "lane" del Tablero de Retrospectiva (spec 006, RF-6). Props tipables (listo para TS, §6.10). El
// reorder es SIEMPRE vertical dentro de esta columna (`Droppable` propio); el pase entre columnas está
// deshabilitado (FA-3) — lo resuelve el padre en `onDragEnd` (RetroBoard.jsx) rechazando cualquier drop
// con `droppableId` distinto al de origen, así que aquí no hace falta `isDropDisabled` dinámico.
//
// Props:
//  - columna: { IdColumna, Codigo, Nombre, Orden }
//  - tarjetas: tarjeta[] (ya filtradas por IdColumna y ordenadas por Orden)
//  - votantesPorTarjeta: { [IdTarjeta]: { votos: string[], likes: string[] } }
//  - readOnly, remainingBudget: ver RetroCard
//  - writingEnabled: boolean (timer corriendo, RF-26 UX — gobierna agregar/editar/eliminar tarjeta)
//  - onAddCard, onEditCard, onDeleteCard, onVoteAdd, onVoteRemove, onLikeToggle: callbacks
//  - t: i18next (namespace "retroBoard")
const RetroColumn = ({
  columna,
  tarjetas = [],
  votantesPorTarjeta = {},
  readOnly = false,
  writingEnabled = true,
  remainingBudget = 0,
  onAddCard,
  onEditCard,
  onDeleteCard,
  onVoteAdd,
  onVoteRemove,
  onLikeToggle,
  t,
}) => {
  const canWrite = !readOnly && writingEnabled;
  // Reordenar (priorizar) se habilita con el tablero ABIERTO, sin depender del timer — igual que los likes.
  // Crear/editar/eliminar tarjeta sí requieren el timer corriendo (canWrite).
  const canReorder = !readOnly;
  const nombreColumna = t(`retro_col_${(columna.Codigo || "").toLowerCase()}`, columna.Nombre);

  return (
    <div className="retro-column h-100 d-flex flex-column">
      <div className="retro-column-header d-flex align-items-center justify-content-between px-2 py-2 mb-2 rounded bg-light">
        <h6 className="mb-0 fw-bold">{nombreColumna}</h6>
        <span className="badge bg-secondary">{tarjetas.length}</span>
      </div>

      <Droppable droppableId={columna.IdColumna}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`retro-column-body flex-grow-1 px-1 py-1 rounded ${
              snapshot.isDraggingOver ? "retro-column-dragover" : ""
            }`}
            style={{ minHeight: "120px" }}
          >
            {tarjetas.length === 0 ? (
              <p className="text-muted text-center small mt-3">{t("retro_empty_column_desc")}</p>
            ) : (
              tarjetas.map((tarjeta, index) => (
                <Draggable
                  draggableId={tarjeta.IdTarjeta}
                  index={index}
                  key={tarjeta.IdTarjeta}
                  isDragDisabled={!canReorder}
                >
                  {(dragProvided) => (
                    <RetroCard
                      tarjeta={tarjeta}
                      votantesNombres={(votantesPorTarjeta[tarjeta.IdTarjeta] || {}).votos || []}
                      likersNombres={(votantesPorTarjeta[tarjeta.IdTarjeta] || {}).likes || []}
                      readOnly={readOnly}
                      writingEnabled={writingEnabled}
                      canAddVote={remainingBudget > 0}
                      onVoteAdd={() => onVoteAdd(tarjeta)}
                      onVoteRemove={() => onVoteRemove(tarjeta)}
                      onLikeToggle={() => onLikeToggle(tarjeta)}
                      onEdit={() => onEditCard(tarjeta)}
                      onDelete={() => onDeleteCard(tarjeta)}
                      innerRef={dragProvided.innerRef}
                      draggableProps={dragProvided.draggableProps}
                      dragHandleProps={dragProvided.dragHandleProps}
                      t={t}
                    />
                  )}
                </Draggable>
              ))
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>

      {canWrite && (
        <Button
          variant="outline-primary"
          size="sm"
          className="mt-2"
          onClick={() => onAddCard(columna)}
        >
          <i className="fa fa-plus-circle mr-2" />
          {t("retro_card_btn_add")}
        </Button>
      )}
    </div>
  );
};

export default RetroColumn;
